// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchParentStudents, fetchProfileImage, normalizeProfileImageFilename, uploadProfileImage } from '../../services/api';
import StudentNameOrderToggle from '../../components/StudentNameOrderToggle';
import { sortStudentsByName, useStudentNameOrder } from '../../utils/studentSort';
import ClearViewPage from '../ClearViewPage';
import WorkGallery from '../../components/WorkGallery';

const gradeOrder = (grade) => {
  const value = String(grade || '').toUpperCase();
  if (value === 'PK') return -2;
  if (value === 'K') return -1;
  const n = Number(value);
  return Number.isNaN(n) ? 999 : n;
};

const getStudentLabel = (student) => {
  return `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`;
};

const getInitials = (student) => {
  const first = String(student.first_name || '').trim();
  const last = String(student.last_name || '').trim();
  const fallback = String(student.student_id || '?');
  return `${first[0] || fallback[0] || '?'}${last[0] || ''}`.toUpperCase();
};

const mergeFamilyMembers = (existing = [], incoming = []) => {
  const byKey = new Map();
  [...existing, ...incoming].forEach((member) => {
    const key = String(member?.email || member?.user_id || member?.parent_id || '').toLowerCase();
    if (!key) return;
    byKey.set(key, member);
  });
  return Array.from(byKey.values());
};

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read image file.'));
    reader.readAsDataURL(file);
  });
}

function applyStudentAvatarMap(students, setAvatarMap) {
  setAvatarMap((prev) => {
    const next = { ...prev };
    (students || []).forEach((student) => {
      if (student.avatar_url || student.avatar_filename) {
        next[student.student_id] = {
          ...next[student.student_id],
          remoteFilename: normalizeProfileImageFilename(student.avatar_filename || student.avatar_url) || next[student.student_id]?.remoteFilename || '',
        };
      }
    });
    return next;
  });
}

export default function StudentProfiles() {
  const location = useLocation();
  const [students, setStudents] = useState<any>([]);
  const [nameOrder] = useStudentNameOrder();
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');

  const [selectedStudentId, setSelectedStudentId] = useState(() => location.state?.selectedStudentId || null);

  const [searchQuery, setSearchQuery] = useState<any>('');
  const [selectedGrade, setSelectedGrade] = useState<any>('');

  const [avatarMap, setAvatarMap] = useState<any>({});
  const [avatarUploadingFor, setAvatarUploadingFor] = useState<any>(null);
  const [avatarMessage, setAvatarMessage] = useState<any>('');

  const snapshotRef = useRef<any>(null);

  const scrollToSnapshot = () => {
    // Smooth scroll so families immediately see the student profile & snapshot
    requestAnimationFrame(() => {
      snapshotRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchParentStudents()
      .then((data) => {
        const nextStudents = data.students || [];
        setStudents(nextStudents);
        applyStudentAvatarMap(nextStudents, setAvatarMap);
      })
      .catch(() => setError('Could not load linked child profiles.'))
      .finally(() => setLoading(false));
  }, []);

  const normalizedStudents = useMemo(() => {
    return (students || [])
      .map((student) => ({
        ...student,
        grade_level: student.grade_level ?? student.classes?.[0]?.grade_level ?? '',
        family_members: mergeFamilyMembers([], student.family_members || []),
        classes: [...(student.classes || [])].sort((a, b) => {
          const subjectCmp = String(a.subject_name || a.course_title || '').localeCompare(String(b.subject_name || b.course_title || ''));
          if (subjectCmp !== 0) return subjectCmp;
          return String(a.class_name || `Class ${a.class_id}`).localeCompare(String(b.class_name || `Class ${b.class_id}`));
        }),
      }));
  }, [students]);

  useEffect(() => {
    const studentsWithSavedAvatars = normalizedStudents.filter((student) => student.avatar_filename || student.avatar_url);
    let cancelled = false;
    const objectUrls = [];
    setAvatarMap((previous) => {
      const next = { ...previous };
      studentsWithSavedAvatars.forEach((student) => {
        next[student.student_id] = { ...next[student.student_id], avatarUrl: '', imageFailed: false };
      });
      return next;
    });

    Promise.all(studentsWithSavedAvatars.map(async (student) => {
      try {
        const blob = await fetchProfileImage(normalizeProfileImageFilename(student.avatar_filename || student.avatar_url));
        const avatarUrl = URL.createObjectURL(blob);
        objectUrls.push(avatarUrl);
        return { studentId: student.student_id, avatarUrl };
      } catch (_err: any) {
        return null;
      }
    })).then((loadedAvatars) => {
      if (cancelled) return;
      setAvatarMap((previous) => {
        const next = { ...previous };
        loadedAvatars.filter(Boolean).forEach(({ studentId, avatarUrl }) => {
          next[studentId] = { ...next[studentId], avatarUrl, imageFailed: false };
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [normalizedStudents]);

  const sortedStudents = useMemo(() => sortStudentsByName(normalizedStudents, nameOrder), [normalizedStudents, nameOrder]);

  const gradeOptions = useMemo(() => {
    const values = new Set();
    normalizedStudents.forEach((student) => {
      if (student.grade_level !== null && student.grade_level !== undefined && String(student.grade_level).trim() !== '') {
        values.add(String(student.grade_level));
      }
    });
    return Array.from(values).sort((a, b) => gradeOrder(a) - gradeOrder(b));
  }, [normalizedStudents]);

  const filteredStudents = useMemo(() => {
    let result = sortedStudents;

    if (selectedGrade) {
      result = result.filter((student) => String(student.grade_level) === selectedGrade);
    }

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter((student) => {
        const studentName = getStudentLabel(student).toLowerCase();
        const subjectMatch = (student.classes || []).some((cls) => String(cls.subject_name || cls.course_title || '').toLowerCase().includes(query));
        return studentName.includes(query) || subjectMatch;
      });
    }

    return result;
  }, [sortedStudents, selectedGrade, searchQuery]);

  useEffect(() => {
    if (!filteredStudents.length) {
      setSelectedStudentId(null);
      return;
    }

    if (!selectedStudentId || !filteredStudents.some((student) => String(student.student_id) === String(selectedStudentId))) {
      setSelectedStudentId(filteredStudents[0].student_id);
    }
  }, [filteredStudents, selectedStudentId]);

  useEffect(() => {
    if (location.state?.scrollToSnapshot && selectedStudentId && !loading) {
      scrollToSnapshot();
    }
  }, [location.state?.scrollToSnapshot, selectedStudentId, loading]);

  const selectedStudent = useMemo(() => {
    return filteredStudents.find((student) => String(student.student_id) === String(selectedStudentId)) || null;
  }, [filteredStudents, selectedStudentId]);

  const uploadAvatar = async (student, file) => {
    if (!student || !file) return;

    if (!file.type.startsWith('image/')) {
      setAvatarMessage('Please upload an image file (PNG or JPG).');
      return;
    }

    setAvatarUploadingFor(student.student_id);
    setAvatarMessage('');

    try {
      const previewDataUrl = await readFileAsDataUrl(file);
      let remoteFilename = '';
      try {
        const result = await uploadProfileImage(student.student_id, file);
        remoteFilename = result?.avatar_filename || result?.filename || result?.file_name || '';
      } catch (_err: any) {
        // Keep local preview even if remote upload mapping is unavailable.
      }

      setAvatarMap((prev) => ({
        ...prev,
        [student.student_id]: {
          previewDataUrl,
          remoteFilename,
          avatarUrl: '',
          imageFailed: false,
          updatedAt: Date.now(),
        },
      }));
      setAvatarMessage('Profile avatar updated.');
    } catch (_err: any) {
      setAvatarMessage('Could not upload this image. Please try another file.');
    } finally {
      setAvatarUploadingFor(null);
    }
  };

  const markAvatarFailed = (studentId) => {
    setAvatarMap((previous) => ({
      ...previous,
      [studentId]: { ...previous[studentId], previewDataUrl: '', avatarUrl: '', imageFailed: true },
    }));
  };

  const panelStyle = {
    background: 'var(--ae-surface)',
    border: '1px solid var(--ae-border)',
    borderRadius: 14,
    boxShadow: 'var(--ae-shadow-card)',
  };

  const selectedStudentTeachers = Array.from(new Map(
    (selectedStudent?.classes || [])
      .filter((item) => item.teacher_name)
      .map((item) => [item.teacher_id || item.teacher_name, item.teacher_name]),
  ).values());

  if (loading) return <div style={{ padding: 32, color: 'var(--ae-text-muted)' }}>Loading child profiles...</div>;

  if (!normalizedStudents.length) {
    return (
      <div style={{ padding: 6 }}>
        <div className="ae-page-hero">
          <h1>My Child Profiles</h1>
          <p>Choose your child to see their full grade-by-grade proficiency progression.</p>
        </div>
        <div style={{ ...panelStyle, padding: 18, color: 'var(--ae-text-muted)' }}>
          {error || 'No child associations found. Ask the teacher to link your account to your child.'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 6, display: 'grid', gap: 18 }}>
      <div className="ae-page-hero" style={{ marginBottom: 0 }}>
        <h1>My Child Profiles</h1>
        <p>Select your child to see their full grade-by-grade proficiency progression.</p>
      </div>

      <div style={{ ...panelStyle, padding: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by child or subject..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          style={{
            flex: '1 1 320px',
            minWidth: 260,
            padding: '9px 11px',
            border: '1px solid var(--ae-border)',
            borderRadius: 10,
            fontSize: 13,
            boxSizing: 'border-box',
            background: 'var(--ae-surface)',
          }}
        />
        <select
          aria-label="Filter by grade"
          value={selectedGrade}
          onChange={(event) => setSelectedGrade(event.target.value)}
          style={{
            minWidth: 140,
            padding: '9px 11px',
            border: '1px solid var(--ae-border)',
            borderRadius: 10,
            fontSize: 13,
            background: 'var(--ae-surface)',
          }}
        >
          <option value="">All Grades</option>
          {gradeOptions.map((grade) => (
            <option key={grade} value={grade}>Grade {grade}</option>
          ))}
        </select>
        <StudentNameOrderToggle />
        <button
          type="button"
          onClick={() => {
            setSearchQuery('');
            setSelectedGrade('');
          }}
          style={{
            background: 'var(--ae-surface-soft)',
            border: '1px solid var(--ae-border)',
            borderRadius: 10,
            padding: '9px 12px',
            fontSize: 12,
            fontWeight: 700,
            color: '#334155',
            cursor: 'pointer',
          }}
        >
          Clear
        </button>
      </div>

      {error && <div style={{ color: '#b91c1c' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(340px, 0.8fr)', gap: 18, minHeight: 420 }}>
        <section style={{ ...panelStyle, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 800, color: 'var(--ae-text-muted)', marginBottom: 4 }}>My Children</div>
              <div style={{ fontSize: 12, color: 'var(--ae-text-muted)' }}>
                Click a child card, then choose a subject.
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ae-primary-strong)', background: 'var(--ae-surface-soft)', border: '1px solid var(--ae-border)', borderRadius: 999, padding: '5px 10px', whiteSpace: 'nowrap' }}>
              Showing {filteredStudents.length} {filteredStudents.length === 1 ? 'child' : 'children'}
            </div>
          </div>
          <div style={{ maxHeight: 560, overflowY: 'auto', paddingRight: 2 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(145px, 1fr))', gap: 12 }}>
            {filteredStudents.map((student) => {
              const isActive = String(student.student_id) === String(selectedStudentId);
              const avatar = avatarMap[student.student_id]?.imageFailed ? '' : (avatarMap[student.student_id]?.previewDataUrl || avatarMap[student.student_id]?.avatarUrl);
              return (
                <button
                  key={student.student_id}
                  type="button"
                  className="ae-child-card"
                  onClick={() => {
                    setSelectedStudentId(student.student_id);
                    scrollToSnapshot();
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    border: isActive ? '1px solid #3f67b7' : '1px solid var(--ae-border)',
                    background: isActive ? 'linear-gradient(180deg, #fff0e3 0%, #ffd9bb 100%)' : 'var(--ae-surface)',
                    borderRadius: 16,
                    padding: 14,
                    cursor: 'pointer',
                    boxShadow: 'var(--ae-shadow-card)',
                    minHeight: 168,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 78,
                        height: 78,
                        borderRadius: '50%',
                        background: '#dbeafe',
                        color: '#1d4ed8',
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        border: '1px solid #bfdbfe',
                        flex: '0 0 auto',
                      }}
                    >
                      {avatar ? (
                        <img src={avatar} alt={`${getStudentLabel(student)} avatar`} onError={() => markAvatarFailed(student.student_id)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span>{getInitials(student)}</span>
                      )}
                    </div>
                    <div style={{ minWidth: 0, width: '100%' }}>
                      <div style={{ fontWeight: 700, color: '#1e3a5f', fontSize: 14, lineHeight: 1.25 }}>{getStudentLabel(student)}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        Grade {student.grade_level ?? 'N/A'}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: isActive ? '#9a3412' : '#64748b' }}>
                        {(student.classes || []).length} subject{(student.classes || []).length === 1 ? '' : 's'}
                      </div>
                      {!!(student.classes || []).length && (
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6 }}>
                          {(student.classes || []).slice(0, 3).map((item) => (
                            <span key={`${student.student_id}-${item.class_id}-${item.subject_name || item.course_title || 'subject'}`} style={{ fontSize: 10, fontWeight: 700, color: '#334155', background: '#ffffff', borderRadius: 999, padding: '4px 8px', border: '1px solid #dbe4f0' }}>
                              {item.subject_name || item.course_title || item.class_name || 'Subject'}
                            </span>
                          ))}
                          {(student.classes || []).length > 3 && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#475569', background: '#f1f5f9', borderRadius: 999, padding: '4px 8px', border: '1px solid #dbe4f0' }}>
                              +{(student.classes || []).length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          </div>
        </section>

        <section style={{ ...panelStyle, padding: 16 }}>
          {!selectedStudent ? (
            <div style={{ color: 'var(--ae-text-muted)' }}>Select a child to view subjects and progression options.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 18, alignItems: 'start', marginBottom: 16 }}>
                <div
                  style={{
                    width: 120,
                    minHeight: 120,
                    borderRadius: 16,
                    background: 'linear-gradient(180deg, #ffe0c6 0%, #ffd1ad 100%)',
                    border: '1px solid #ffc089',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#1d4ed8',
                    fontWeight: 800,
                    fontSize: 24,
                    padding: 10,
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#dbeafe', border: '1px solid #bfdbfe', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {avatarMap[selectedStudent.student_id]?.previewDataUrl ? (
                    <img
                      src={avatarMap[selectedStudent.student_id]?.previewDataUrl}
                      alt={`${getStudentLabel(selectedStudent)} avatar`}
                      onError={() => markAvatarFailed(selectedStudent.student_id)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : avatarMap[selectedStudent.student_id]?.avatarUrl ? (
                    <img
                      src={avatarMap[selectedStudent.student_id]?.avatarUrl}
                      alt={`${getStudentLabel(selectedStudent)} avatar`}
                      onError={() => markAvatarFailed(selectedStudent.student_id)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span>{getInitials(selectedStudent)}</span>
                  )}
                  </div>
                  <div style={{ marginTop: 10, width: '100%', fontSize: 11, lineHeight: 1.35, color: '#9a3412', textAlign: 'left' }}>
                    <div style={{ fontWeight: 800, color: '#1e3a5f' }}>{getStudentLabel(selectedStudent)}</div>
                    <div>Grade {selectedStudent.grade_level ?? 'N/A'}</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  <div style={{ padding: '12px 14px', borderRadius: 14, background: '#ffffff', border: '1px solid #dbe4f0', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.07)' }}>
                    <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 800, color: '#64748b', marginBottom: 6 }}>Student Profile</div>
                    <h2 style={{ margin: '0 0 6px', fontSize: 22, color: '#1e3a5f' }}>{getStudentLabel(selectedStudent)}</h2>
                    <div style={{ fontSize: 13, color: '#64748b', paddingBottom: 8, borderBottom: '1px solid #e5edf7' }}>
                      Grade: {selectedStudent.grade_level ?? 'N/A'}
                      {selectedStudent.relationship_type ? ` • Relationship: ${selectedStudent.relationship_type}` : ''}
                    </div>
                    {!!selectedStudentTeachers.length && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
                        <strong style={{ color: '#1e3a5f' }}>Teachers:</strong> {selectedStudentTeachers.join(', ')}
                      </div>
                    )}
                    {!!selectedStudent.family_members?.length && (
                      <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                        <strong style={{ color: '#1e3a5f', fontSize: 12 }}>Family Members</strong>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
                          {selectedStudent.family_members.slice(0, 2).map((member, index) => (
                            <div key={`${member.email || member.user_id || member.parent_id}`} style={{ padding: '10px 12px', borderRadius: 10, background: '#ffffff', border: '1px solid #dbe4f0', fontSize: 11, color: '#475569', minHeight: 108, boxShadow: '0 6px 14px rgba(15, 23, 42, 0.05)' }}>
                              <div style={{ fontWeight: 800, color: '#64748b', marginBottom: 6 }}>Guardian #{index + 1}</div>
                              <div style={{ lineHeight: 1.45, fontWeight: 700, color: '#1e3a5f' }}>{`${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Family Member'}</div>
                              <div style={{ lineHeight: 1.45, marginTop: 3 }}>{member.relationship_type || 'parent'}</div>
                              <div style={{ lineHeight: 1.45, marginTop: 3 }}>{member.email || 'No email listed'}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <label
                    htmlFor={`parent-avatar-upload-${selectedStudent.student_id}`}
                    style={{
                      display: 'inline-block',
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      borderRadius: 9,
                      padding: '7px 11px',
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#334155',
                      cursor: avatarUploadingFor === selectedStudent.student_id ? 'default' : 'pointer',
                      opacity: avatarUploadingFor === selectedStudent.student_id ? 0.7 : 1,
                    }}
                  >
                    {avatarUploadingFor === selectedStudent.student_id ? 'Uploading...' : 'Upload Profile Avatar'}
                  </label>
                  <input
                    id={`parent-avatar-upload-${selectedStudent.student_id}`}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      uploadAvatar(selectedStudent, file);
                      event.target.value = '';
                    }}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              {avatarMessage && (
                <div
                  style={{
                    marginBottom: 12,
                    color: avatarMessage.includes('Could not') || avatarMessage.includes('Please') ? '#b91c1c' : '#166534',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {avatarMessage}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {selectedStudent && (
        <div
          ref={snapshotRef}
          id="student-progress-snapshot"
          style={{ ...panelStyle, padding: 16, scrollMarginTop: 24 }}
        >
          <ClearViewPage
            studentId={selectedStudent.student_id}
            studentName={getStudentLabel(selectedStudent)}
            role="parent"
          />
        </div>
      )}

      {selectedStudent && (
        <div style={{ ...panelStyle, padding: 16 }}>
          <WorkGallery studentId={selectedStudent.student_id} heading="Work gallery" />
        </div>
      )}
    </div>
  );
}
